import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
  Button, TextInput, Textarea, Select, Alert, FilterChip,
} from '@parsina/framework';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT, autoDir } from '../../../i18n/I18nProvider.jsx';
import { surfacesFor, MATERIALS } from '../../../lib/teeth.js';
import {
  chartEventsFor, chartEffectFor, chartEffectOptions, parseChartEffect,
  CHART_EFFECT_NONE, sessionsOf,
} from '../Visit.shared.js';
import { listSeparator } from '../../../i18n/format';

// «ثبت انجام» — the work, written down.
//
// ── The thing this app could not do ─────────────────────────────────────────
// A dentist could record what is WRONG with a tooth and what they PLAN to do
// about it, and had nowhere at all to say what they actually did. «انجام‌شده»
// on a plan line was the closest thing, and it is a status: no who, no when, no
// visit, no surfaces, no material, no note, and reversible without a trace.
// This form is the missing half, and it is why `done` left that menu.
//
// ── What it is NOT ──────────────────────────────────────────────────────────
// It is not the tooth editor. `ToothDrawer` states what IS TRUE of a tooth;
// this states what WAS DONE to it in this visit. They meet in exactly one
// place — «وضعیت دندان پس از درمان» below, which offers the chart change the
// treatment caused — and that offer is a proposal the dentist confirms, never
// something derived behind them. A screen that silently charted «MOD composite
// ⇒ three restored surfaces» would have the chart asserting what nobody looked
// at, and a dentist would find it there months later with no idea who said so.
//
// That block used to appear only when SURFACES were ticked, which is why a
// عصب‌کشی, a روکش and a کشیدن left the odontogram untouched: the work was
// recorded, billed and invisible on the one picture of the mouth. It now always
// appears for a treatment on a tooth, prefilled from what the catalogue says
// the service does.
//
// ── No money, anywhere ──────────────────────────────────────────────────────
// There is no amount on this form and none on the row it writes. The billable
// act is an order in clinicalencounter, and the record's `billingState` reports
// how far that separate write got. A field here would be a second, disagreeing
// opinion about what the patient owes, typed by whoever happened to be holding
// the mouse.
//
// ── One session, not a course ───────────────────────────────────────────────
// A three-session root canal is ONE plan line quoted at three (D6) and THREE
// records. «نتیجه» is what says which one finished it: تکمیل شد closes the
// line, ادامه در جلسهٔ بعد leaves it open for the next appointment. That is
// the whole of the session model, and why «جلسهٔ ۲ از ۳» is derived rather
// than stored.

/** The surfaces a tooth actually has — an incisor has I, a molar has O. */
const surfaceCodes = (toothFdi) => (toothFdi ? surfacesFor(toothFdi) : []);

export default function TreatmentRecordDialog({
  open, onOpenChange, onRecord, saving, t, formatNumber,
  // The plan line being carried out. Never null: «ثبت انجام» is offered ON a
  // line, and «ثبت درمانِ خارج از برنامه» — the one entry point that opened this
  // without one — was removed as a control nobody used. Work that was not
  // planned is recorded by adding the line and then recording it, which is one
  // more press and one fewer way to end up with a treatment nothing quoted.
  item,
  // The COURSE that line belongs to. Separate from `item` because a plan item
  // does not carry its own plan's id — the service returns them nested — and
  // reading `item.planId` silently recorded every session against no plan at
  // all, which is the one field that ties a record back to what was agreed.
  planId,
  // The catalogue entry behind that line — its name, and the performers the
  // clinic named for it (D7).
  entry,
  records = [],
}) {
  const [form, setForm] = React.useState({
    catalogId: '', serviceName: '', toothFdi: '', surfaces: '',
    material: '', note: '', outcome: 'completed', chartEffect: CHART_EFFECT_NONE,
  });
  // Whether the dentist has touched «وضعیت دندان پس از درمان» themselves. Once
  // they have, changing «نتیجه» must not overwrite their answer: the C4 rule
  // (a session that does not finish the work proposes nothing) is a DEFAULT,
  // and a default that keeps reasserting itself over a person is a control
  // that fights back.
  const [effectTouched, setEffectTouched] = React.useState(false);
  const [failure, setFailure] = React.useState(null);

  // Reset on every OPEN — the dialog is kept mounted by its parent, so without
  // this the surfaces of the last tooth greet the next one.
  React.useEffect(() => {
    if (!open) return;
    const sessions = item ? sessionsOf(item, records) : null;
    setForm({
      catalogId: item?.catalogId || '',
      serviceName: '',
      toothFdi: item?.toothFdi || '',
      // What was QUOTED for this line, as the starting point. A dentist who
      // treated two of the three surfaces corrects it; one who treated all
      // three does not have to type them again.
      surfaces: item?.surfaces || '',
      material: '',
      note: '',
      // The last session of a course defaults to «تکمیل شد» and the ones before
      // it to «ادامه در جلسهٔ بعد» — which is what actually happens, and means
      // the common case needs no thought.
      outcome: sessions && sessions.done + 1 < sessions.quoted ? 'in_progress' : 'completed',
      // What the catalogue says this service does to the tooth — «عصب‌کشی مولر»
      // ⇒ عصب‌کشی‌شده. The form cannot work that out, and before the catalogue
      // carried it the only thing it could propose was a surface.
      chartEffect: chartEffectFor(
        entry,
        sessions && sessions.done + 1 < sessions.quoted ? 'in_progress' : 'completed',
      ),
    });
    setEffectTouched(false);
    setFailure(null);
  }, [open, item, entry, records]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // «نتیجه» moves the proposal with it, until the dentist has answered it
  // themselves: a session that does not finish the work proposes nothing (C4),
  // and going back to «تکمیل شد» must bring the proposal back rather than
  // leaving a finished root canal unrecorded on the chart.
  const setOutcome = (outcome) => setForm((f) => ({
    ...f,
    outcome,
    chartEffect: effectTouched ? f.chartEffect : chartEffectFor(entry, outcome),
  }));

  // The catalogue row behind the line, for its «کاربرد» snapshot.
  const chosen = entry || null;

  const codes = surfaceCodes(form.toothFdi);
  const chosenSurfaces = new Set(String(form.surfaces || '').toUpperCase().split(''));
  const toggleSurface = (code) => {
    const next = new Set(chosenSurfaces);
    if (next.has(code)) next.delete(code); else next.add(code);
    // Canonical ORDER, not tap order: the service normalises it anyway, and a
    // «DOM» on the screen that becomes «MDO» in the record reads as a bug.
    set({ surfaces: codes.filter((c) => next.has(c)).join('') });
  };

  // What the block would write, in the dentist's own words. Shown rather than
  // merely offered: «نمودار دندان هم به‌روز شود» told nobody WHAT it would say.
  const chartEvents = chartEventsFor({
    toothFdi: form.toothFdi,
    surfaces: form.surfaces,
    material: form.material,
    effect: form.chartEffect,
  });
  const effect = parseChartEffect(form.chartEffect);
  // A surface effect with nothing ticked writes nothing. Saying so is the
  // point: silence here is exactly what made this feature necessary.
  const effectWritesNothing = !!effect && chartEvents.length === 0;
  const effectLabel = (option) => {
    const word = t(`tooth.value.${option.word}`);
    return option.layer === 'surface'
      ? t('visit.record.chartEffect.surfaceOption', { state: word })
      : word;
  };

  const submit = async () => {
    setFailure(null);

    const { error } = await onRecord({
      planId: planId || null,
      planItemId: item?.id || null,
      catalogId: form.catalogId || item?.catalogId || null,
      // The SNAPSHOT of what was done, taken here because here is the only
      // place that has it: the service defaults nothing — it stores the name it
      // is given and accepts a catalog_id alone — so sending nothing wrote a
      // record that reads «اقدام بدون عنوان» for ever. The dentist's own
      // wording wins, then the plan line's, then the catalogue's.
      serviceName: form.serviceName.trim() || item?.description || entry?.name || null,
      applicability: chosen?.applicability || null,
      toothFdi: form.toothFdi || null,
      surfaces: form.surfaces || null,
      material: form.material || null,
      note: form.note.trim() || null,
      outcome: form.outcome,
      chartEvents,
    });
    if (error) {
      setFailure(error);
      return;
    }
    onOpenChange(false);
  };

  const title = item?.description || t('visit.plan.untitledItem');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="treatment-record-dialog">
        <DialogHeader>
          <DialogTitle>{t('visit.record.title', { treatment: title })}</DialogTitle>
          <DialogDescription>{t('visit.record.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {failure && (
              <Alert variant="error">
                {failure.message || t('visit.record.failed')}
              </Alert>
            )}

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10,
            }}>
              <TextInput
                label={t('visit.record.tooth')}
                aria-label={t('visit.record.tooth')}
                dir="ltr"
                inputMode="numeric"
                value={form.toothFdi}
                placeholder={t('visit.plan.wholeMouthShort')}
                onChange={(e) => set({ toothFdi: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              />
              <Select
                label={t('visit.record.outcome')}
                aria-label={t('visit.record.outcome')}
                data-testid="record-outcome"
                value={form.outcome}
                placeholder={null}
                onChange={(e) => setOutcome(e.target.value)}
                options={[
                  { value: 'completed', label: t('visit.record.outcome.completed') },
                  { value: 'in_progress', label: t('visit.record.outcome.inProgress') },
                ]}
              />
            </div>

            {/* The same pills the tooth editor uses, and only the surfaces this
                tooth HAS: an incisor is offered I, a molar O. */}
            {codes.length > 0 && (
              <div>
                <div style={{ fontSize: 12.5, color: C.textSecondary, marginBottom: 6 }}>
                  {t('visit.record.surfaces')} <span style={{ color: C.textMuted }}>{t('common.form.optional')}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid="record-surfaces">
                  {codes.map((code) => (
                    <FilterChip
                      key={code}
                      active={chosenSurfaces.has(code)}
                      onClick={() => toggleSurface(code)}
                      // M D F L O I is the notation itself, not a word to
                      // translate — the tooth editor shows the same letters.
                      aria-label={code}
                      label={<span {...LTR_TEXT}>{code}</span>}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* WHO DID THE WORK is deliberately NOT asked here yet, and the
                omission is recorded rather than silent. D7 wants the performers
                the clinic named on the service; `TreatmentPerformer` carries
                `{ type, id, isDefault }` and no NAME, so a picker built on it
                today would offer a dentist a list of UUIDs. Until the BFF
                resolves them, the record names the dentist who wrote it — which
                is what the service defaults to and is true far more often than
                not. */}
            <Select
              label={`${t('visit.record.material')} ${t('common.form.optional')}`}
              aria-label={t('visit.record.material')}
              value={form.material}
              placeholder={t('visit.record.noMaterial')}
              onChange={(e) => set({ material: e.target.value })}
              options={MATERIALS.map((m) => ({ value: m, label: t(`tooth.material.${m}`) }))}
            />

            <Textarea
              label={`${t('visit.record.note')} ${t('common.form.optional')}`}
              aria-label={t('visit.record.note')}
              rows={2}
              dir={autoDir(form.note)}
              value={form.note}
              placeholder={t('visit.record.notePlaceholder')}
              onChange={(e) => set({ note: e.target.value })}
            />

            {/* ── «وضعیت دندان پس از درمان» ─────────────────────────────────
                The one place this form touches the chart, and it says out loud
                what it would write.

                ALWAYS here for a treatment on a tooth — that is the fix. While
                this block appeared only when surfaces were ticked, a عصب‌کشی,
                a روکش and a کشیدن were recorded, billed, and left the chart
                saying the tooth was untouched.

                Not rendered for whole-mouth work (C8): a جرم‌گیری changes no
                tooth, and a picker offering to chart one would be asking a
                question with no right answer. */}
            {form.toothFdi && (
              <div
                data-testid="record-chart-effect"
                style={{
                  border: `1px solid ${C.border}`, borderRadius: 10,
                  padding: '10px 12px', background: CT.surface2,
                }}
              >
                <Select
                  label={t('visit.record.chartEffect')}
                  aria-label={t('visit.record.chartEffect')}
                  value={form.chartEffect}
                  placeholder={null}
                  onChange={(e) => {
                    setEffectTouched(true);
                    set({ chartEffect: e.target.value });
                  }}
                  options={chartEffectOptions(form.toothFdi).map((o) => ({
                    value: o.value,
                    label: o.layer === 'none'
                      ? t('visit.record.chartEffect.none')
                      : effectLabel(o),
                  }))}
                />

                <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 6 }}>
                  {!effect
                    ? t('visit.record.chartEffect.noneDetail')
                    : effectWritesNothing
                      ? t('visit.record.chartEffect.needsSurfaces')
                      // Two sentences, not one with an optional tail: a
                      // surface effect names the surfaces INSIDE the sentence,
                      // and a whole-tooth one has none to name. One sentence
                      // with «{{surfaces}}» glued on the end read as a bare
                      // «M، O» after the full stop.
                      : t(
                        effect.layer === 'surface'
                          ? 'visit.record.chartEffect.detailSurfaces'
                          : 'visit.record.chartEffect.detail',
                        {
                          tooth: formatNumber(form.toothFdi),
                          state: t(`tooth.value.${effect.value}`),
                          surfaces: chartEvents
                            .map((e) => e.surface)
                            .filter(Boolean)
                            .join(listSeparator()),
                        },
                      )}
                </div>

                {/* Why it is «تغییری در چارت ندهد» by default here. Without the
                    line the form looks like it forgot to ask. */}
                {form.outcome === 'in_progress' && !effect && (
                  <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 4 }}>
                    {t('visit.record.chartEffect.inProgress')}
                  </div>
                )}
              </div>
            )}

            {/* Said once, plainly: this is a clinical record, and the money and
                the stock follow it as separate writes that may not have landed
                yet. Without it a dentist reads «ثبت» as «billed». */}
            <div style={{ fontSize: 11.5, color: C.textMuted }}>
              {t('visit.record.billingNote')}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button className="min-h-11" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            className="min-h-11"
            onClick={submit}
            disabled={saving}
            data-testid="record-save"
          >
            {saving ? t('common.actions.saving') : t('visit.record.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
